<template>
  <v-card class="pa-4" flat>
    <v-row no-gutters>
      <v-col cols="12" md="4">
        <v-card-subtitle class="font-weight-bold">
          کافیست داستان تصویر مورد نظر خودرا وارد نمایید،
          <br />
          تا هوش مصنوعی آن را برای شما خلق کند.
        </v-card-subtitle>
      </v-col>

      <v-col
        class="pa-2 d-flex justify-space-between justify-md-end"
        cols="12"
        md="4"
      >
        <together-ai-upgrade
          v-model="showUpgrade"
          :current-package.sync="currentPackage"
        />

        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-chip
              class="rounded-lg me-2 info--text"
              color="rgba(198, 198, 198, .3)"
              small
              v-bind="attrs"
              @click="showUpgrade = true"
              v-on="on"
            >
              <v-icon left small>mdi-creation-outline</v-icon>
              <span v-if="computedCurrentPackage">{{
                computedCurrentPackage.count || "0"
              }}</span>
            </v-chip>
          </template>

          <span v-if="computedCurrentPackage">
            باقی مانده:
            {{ computedCurrentPackage.count }} خلق تصویر
            {{ computedCurrentPackage.free ? " - رایگان" : "" }}
          </span>
        </v-tooltip>

        <v-menu offset-y open-on-hover>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="rounded-lg text-none"
              depressed
              v-bind="attrs"
              v-on="on"
            >
              <v-icon left>mdi-chevron-down</v-icon>
              {{ langItems[selectedLang].text }}
              <v-icon right>mdi-web</v-icon>
            </v-btn>
          </template>

          <v-list dense>
            <v-subheader>انتخاب زبان برای نوشتن متن درخواست</v-subheader>
            <v-list-item-group v-model="selectedLang" color="primary">
              <v-list-item v-for="(item, i) in langItems" :key="i">
                <v-list-item-icon class="py-1 me-2">
                  {{ item.flag }}
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title v-text="item.text"></v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-menu>
      </v-col>

      <v-col class="py-2" cols="12" md="4">
        <v-select
          v-model="sizeModel"
          :items="sizeItems"
          :menu-props="{ offsetY: true }"
          class="rounded-lg"
          dense
          hide-details
          label="سایز تصویر"
          outlined
        >
          <template v-slot:prepend-inner>
            <v-icon
              >mdi-{{
                sizeItems.find((s) => s.value === sizeModel).icon
              }}</v-icon
            >
          </template>

          <template v-slot:item="{ item }">
            <v-list-item-icon class="me-2">
              <v-icon>mdi-{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{ item.text }}
              </v-list-item-title>
            </v-list-item-content>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <v-textarea
      v-model="prompt"
      :loading="loading"
      :rules="[rules.empty, rules.spellChecker]"
      class="rounded-lg"
      dense
      label="متن درخواست"
      outlined
      placeholder="تصویر مورد نظر خودرا شرح دهید..."
      @input="debounceTranslate"
    >
      <template v-slot:append>
        <v-btn
          :color="regenerate ? 'orange' : 'success'"
          :disabled="!prompt || !allowGenerate"
          :loading="loading || (isWriting && allowGenerate)"
          depressed
          @click="changeInput"
        >
          <v-icon v-if="regenerate" left>mdi-reload</v-icon>
          <v-icon v-else left>mdi-image-auto-adjust</v-icon>
          {{ regenerate ? "ساخت تصویر جدید" : "ساخت تصویر" }}
        </v-btn>
      </template>
    </v-textarea>

    <v-switch v-model="iterativeMode" dense inset label="تولید تصاویر مشابه" />

    <template v-if="!!result.at(indexAt)">
      <v-img
        :src="`data:image/png;base64,${result.at(indexAt).image}`"
        alt=""
        class="rounded-lg"
        contain
        width="100%"
      >
        <div class="pa-2 w-100">
          <v-btn
            :loading="upLoading"
            class="btn-accept rounded-lg ma-0"
            depressed
            @click="saveImage"
          >
            <v-icon left>mdi-content-save-plus-outline</v-icon>
            ذخیره تصویر
          </v-btn>
        </div>
      </v-img>

      <v-card-subtitle class="pb-0">
        فقط تصویر نمایش داده شده در بالا ذخیره خواهد شد.
      </v-card-subtitle>
    </template>

    <div
      class="d-flex align-center py-4"
      style="max-width: 100%; max-height: 300px; overflow-x: auto; gap: 16px"
    >
      <div
        v-for="(item, index) in result"
        :key="index"
        class="rounded-lg overflow-hidden cursor-pointer"
        style="
          user-select: none;
          max-width: 120px;
          width: 120px;
          min-width: 120px;
        "
        @click="indexAt = index"
      >
        <v-img
          :src="`data:image/png;base64,${item.image}`"
          alt=""
          aspect-ratio="1"
          contain
          max-width="100%"
        />
      </div>
    </div>
  </v-card>
</template>

<script>
import axios from "axios";
import { debounce, qrAxios } from "@/main";
import { base64ToBlob, saveOnCdnFiles } from "@/Utilities/ImageGallery";
import SwiperWrapper from "@/view/content/cm/swiperWrapper.vue";
import TogetherAiUpgrade from "@/view/content/pickers/uploadCenter/imageUploader/AIGeneration/togetherAiUpgrade.vue";

export default {
  name: "togetherAiImage",

  components: { SwiperWrapper, TogetherAiUpgrade },

  data() {
    return {
      prompt: "",
      iterativeMode: false,
      showUpgrade: false,
      currentPackage: {},
      loading: false,
      result: [],
      indexAt: -1,
      upLoading: false,
      catName: "تصاویر خلق شده",
      lastPrompt: "",
      sizeModel: "16:9",
      sizeItems: [
        // {value: 'custom', text: 'دلخواه', width: 1024, height: 1024},
        {
          value: "16:9",
          text: "16:9 (افقی)",
          width: 1792,
          height: 1008,
          icon: "panorama-horizontal-outline",
        },
        {
          value: "9:16",
          text: "9:16 (عمودی)",
          width: 1008,
          height: 1792,
          icon: "panorama-vertical-outline",
        },
        {
          value: "1:1",
          text: "1:1 (مربع)",
          width: 1024,
          height: 1024,
          icon: "square-outline",
        },
      ],
      translatedText: "",
      selectedLang: 0,
      isWriting: false,
      langItems: [
        { value: "fa", text: "Fa | فارسی", name: "فارسی", flag: "🇮🇷" },
        { value: "en_US", text: "En | انگلیسی", name: "انگلیسی", flag: "🇬🇧" },
      ],
      rules: {
        empty: (v) => !v || v.length >= 3 || "حداقل ۳ کاراکتر باید وارد شود!",
        spellChecker: (v) =>
          !v ||
          this.spellChecker(v) ||
          `متن وارد شده باید به زبان ${this.computedSelectedLang.name} باشد!`,
      },
    };
  },

  computed: {
    regenerate() {
      let p =
        this.langItems[this.selectedLang].value === "fa"
          ? this.translatedText
          : this.prompt;
      return !!p && this.lastPrompt.trim() === p.trim();
    },
    computedSelectedLang() {
      return this.langItems[this.selectedLang];
    },
    allowGenerate() {
      return typeof this.rules.spellChecker(this.prompt) === "boolean";
    },
    computedCurrentPackage() {
      let packages = this.currentPackage;

      return packages.remaining_images instanceof Array
        ? packages.remaining_images.reduce((obj, curr) => {
            obj.free = false;

            if (!obj.hasOwnProperty("count"))
              obj.count = Number(curr.remaining_images);
            else obj.count += Number(curr.remaining_images);

            console.log("obj: ", obj);

            return obj;
          }, {})
        : {
            free: packages.package_id === "free_quota",
            count: packages.remaining_images,
          };
    },
  },

  watch: {
    selectedLang(val) {
      this.prompt = "";
      this.translatedText = "";
    },
  },

  methods: {
    debounceTranslate() {
      this.isWriting = true;

      debounce(() => {
        let lang = this.langItems[this.selectedLang].value;

        if (lang === "fa") this.translatePrompt();
        else this.isWriting = false;
      }, 2000);
    },
    translatePrompt() {
      axios
        .post("https://api.reverso.net/translate/v1/translation", {
          format: "text",
          from: "per",
          to: "eng",
          input: this.prompt.replace(/\n/g, " "),
          options: {
            sentenceSplitter: true,
            origin: this.$coreqrOrigin,
            contextResults: true,
            languageDetection: true,
          },
        })
        .then(({ data }) => {
          this.translatedText = data.translation[0];
        })
        .catch((err) => console.log(err))
        .finally(() => (this.isWriting = false));
    },
    changeInput() {
      this.loading = true;

      debounce(() => {
        this.getImage();
      }, 1000);
    },
    getImage() {
      let needPack = !this.computedCurrentPackage.count;

      let p =
        this.langItems[this.selectedLang].value === "fa"
          ? this.translatedText
          : this.prompt;
      this.lastPrompt = p;

      let { width, height } = this.sizeItems.find(
        (item) => item.value === this.sizeModel
      );

      if (!width || !height) {
        this.loading = false;
        return this.$toast.warning(
          "لطفا عرض و ارتفاع را به درستی وارد نمایید!"
        );
      }

      qrAxios
        .post("/v1/image-generator", {
          width: Number(width),
          height: Number(height),
          prompt: p,
          steps: 3,
        })
        .then(({ data }) => {
          let { image, ...rest } = data;
          this.currentPackage = { ...rest };
          if (needPack && !rest.remaining_images) {
            this.showUpgrade = true;
            this.$toast.info(rest.message || "خطا در خلق تصویر!");
          }

          if (!!image) {
            this.result.push({
              image: image?.data?.[0]?.b64_json,
              prompt: p,
              checked: false,
            });
            this.indexAt = -1;
          }
        })
        .catch(({ response }) => {
          this.$toast.error(
            response?.data?.error?.message || "خطا در خلق تصویر"
          );
          console.error(response);
        })
        .finally(() => (this.loading = false));
    },
    saveImage() {
      this.upLoading = true;

      // Example Base64 string and mimeType
      const base64String = this.result.at(this.indexAt).image;
      const mimeType = "application/octet-stream"; // Adjust the MIME type as needed

      // Convert Base64 to Blob
      const fileBlob = base64ToBlob(base64String, mimeType);

      // this.result.at(this.indexAt).prompt.replace(/ /g, '_')
      saveOnCdnFiles(
        fileBlob,
        this.result.at(this.indexAt).prompt.replace(/ /g, "_"),
        this.catName
      )
        .then((response) => {
          this.$emit("saved", this.catName);
        })
        .catch((error) => {
          this.$toast.error("خطای آپلود تصویر!");
        })
        .finally(() => (this.upLoading = false));
    },
    spellChecker(input) {
      const reg = {
        en: /[\u0600-\u06FF\u06F0-\u06F9]+/,
        fa: /[A-Za-z]+/,
      };

      return Array.from(input).every(
        (char) => !reg[this.computedSelectedLang.value].test(char)
      );
    },
  },
};
</script>

<style scoped></style>
